import { Trans, useTranslation } from "react-i18next";
import BackgroundPatternDecorative from "#src/assets/images/v2/background-pattern-decorative.svg";
import { ButtonV2 } from "#src/commonV2/Button";
import { ArrowUpRight } from "@untitled-ui/icons-react";
import urls from "#src/config/urls";
import { useWorkspaceList } from "#src/hooks/dataFetchers/useWorkspaceList";
import { useEffect, useMemo } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { pathname } from "#src/config/pathname";
import { useAppSelector } from "#src/store/hooks";
import workspaceServices from "#src/services/workspace";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";

export const Enable2FAPage = () => {
  const { t } = useTranslation("enable2FA");

  const { workspaceId } = useParams<"workspaceId">();
  const userInfo = useAppSelector((state) => state.auth.userInfo);

  const navigate = useNavigate();

  const { workspaceList } = useWorkspaceList();

  const enable2FA = () => {
    window.open(`${urls.ID_CYSTACK_URL}/security/two-step-verification`);
  };

  const notRequireEnabled2faWs = useMemo(() => {
    return workspaceList.find((w) => !w.enabled_2fa);
  }, [workspaceList]);

  const currentWorkspace = useMemo(() => {
    return workspaceList.find((ws) => ws.id == workspaceId);
  }, [workspaceId, workspaceList]);

  const toOtherWorkspace = () => {
    window.location.replace(
      generatePath(pathname.SOLUTIONS, {
        workspaceId: notRequireEnabled2faWs?.id,
      })
    );
  };

  useEffect(() => {
    if (!workspaceId || userInfo === undefined) return;

    const currentWorkspace = workspaceList.find(
      (item) => item.id === workspaceId
    );

    // If current workspace has turned 2fa off or if user has turned their 2fa on.
    const valid2fa =
      (currentWorkspace && !currentWorkspace.enabled_2fa) ||
      userInfo.is_factor2;

    if (valid2fa) {
      workspaceServices
        .retrieve_workspace(workspaceId)
        .then(() => {
          navigate(generatePath(pathname.SOLUTIONS, { workspaceId }));
        })
        .catch(apiErrorHandler);
    }
  }, [workspaceId, workspaceList, userInfo, navigate]);

  return (
    <div
      className="h-screen w-screen flex flex-col justify-center items-center text-center gap-3 whitespace-pre-wrap v2"
      style={{
        backgroundImage: `url(${BackgroundPatternDecorative})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "auto",
        backgroundPositionX: "center",
      }}
    >
      <div className="w-[28rem] flex flex-col gap-5">
        <p className="text-lg font-semibold text-gray-v2-900">
          {t("title", { workspace: currentWorkspace?.name })}
        </p>
        <p className="text-md font-regular text-gray-v2-600">
          <Trans
            t={t}
            i18nKey={"desc"}
            values={{ workspace: currentWorkspace?.name }}
            components={{
              span: <span className="text-md font-semibold text-gray-v2-900" />,
            }}
          />
        </p>
        <p className="text-md font-regular text-gray-v2-600">{t("note")}</p>
        <ButtonV2 className="w-full" variant="primary" onClick={enable2FA}>
          {t("enable")}
        </ButtonV2>
        {!!notRequireEnabled2faWs && (
          <div className="text-center">
            <p className="text-md font-regular text-gray-v2-600">{t("or")}</p>
            <ButtonV2
              className="w-full"
              variant="tertiary"
              color="brand"
              onClick={toOtherWorkspace}
            >
              {t("toOtherWorkspace")}
              <ArrowUpRight className="w-5 h-5" />
            </ButtonV2>
          </div>
        )}
      </div>
    </div>
  );
};
